import React from "react";
import { Link } from "@inertiajs/react";
import {
  Box,
  Stack,
  Group,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Card } from "./UI/Card";
import {
  Heading3,
  MainText,
  Eyebrow,
  BodyText,
} from "./UI/Typography";
import type { Pillar } from "../data/pillars";

// Convert a hex color (#rrggbb) + alpha (0–1) into an rgba() string.
// Mirrors the ProjectCard glow pattern but tinted per pillar accent.
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

interface PillarCardProps {
  pillar: Pillar;
}

export const PillarCard: React.FC<PillarCardProps> = ({ pillar }) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;
  const accentColor = other[pillar.accentColor];
  const accentTextColor = other[pillar.accentTextColor];
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  // Copy of ProjectCard.tsx:66–77 hover/rest pattern, retinted with pillar accent.
  const glassHover = {
    transform: "translateY(-8px)",
    boxShadow: `0 24px 64px ${hexToRgba(accentColor, 0.22)}, 0 0 0 1px ${hexToRgba(accentColor, 0.28)}, inset 0 1px 0 rgba(255,255,255,0.1)`,
    border: `1px solid ${hexToRgba(accentColor, 0.28)}`,
  };

  const glassRest = {
    transform: "translateY(0)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.07), 0 8px 32px rgba(0,0,0,0.4)",
    border: `1px solid ${hexToRgba(accentColor, 0.12)}`,
  };

  const glassActive = {
    transform: "translateY(-4px) scale(0.99)",
    boxShadow: `0 12px 32px ${hexToRgba(accentColor, 0.18)}, 0 0 0 1px ${hexToRgba(accentColor, 0.24)}, inset 0 1px 0 rgba(255,255,255,0.08)`,
    border: `1px solid ${hexToRgba(accentColor, 0.24)}`,
  };

  return (
    <UnstyledButton
      component={Link}
      href={pillar.href}
      style={{
        textDecoration: "none",
        display: "block",
        height: "100%",
        width: "100%",
      }}
    >
      <Card
        padding="lg"
        radius="md"
        h="100%"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setPressed(false);
        }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        style={{
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          ...(pressed ? glassActive : hovered ? glassHover : glassRest),
        }}
      >
        <Stack gap="sm" h="100%">
          <Eyebrow accent={accentTextColor}>{pillar.label}</Eyebrow>
          <Group gap="xs" align="center" wrap="nowrap">
            <Box
              style={{
                width: 24,
                height: 24,
                borderRadius: "var(--mantine-radius-sm)",
                background: `linear-gradient(135deg, ${accentColor}33, ${accentColor}11)`,
                border: `1px solid ${accentColor}33`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <pillar.icon size={14} style={{ color: accentColor }} />
            </Box>
            <Heading3 style={{ margin: 0 }}>{pillar.heading}</Heading3>
          </Group>
          <BodyText
            style={{
              maxWidth: "none",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {pillar.body}
          </BodyText>
          <Group gap="xs" mt="auto" style={{ color: accentTextColor }}>
            <MainText
              size="sm"
              style={{ color: accentTextColor, fontWeight: 600 }}
            >
              {pillar.cta}
            </MainText>
            <IconArrowRight size={16} style={{ color: accentTextColor }} />
          </Group>
        </Stack>
      </Card>
    </UnstyledButton>
  );
};
